
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Navigation */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/d6f638fe-d95c-4ddc-bf03-ccf15d6b67af.png" 
              alt="PAKO d.o.o. Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className={`text-2xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              PAKO d.o.o.
            </span>
          </div>
          <ul className="flex items-center gap-10">
            {['About', 'Products', 'Features', 'Contact'].map((item) => (
              <li key={item}>
                <button
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'
                  }`}
                >
                  {item}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-between px-16 overflow-hidden bg-gradient-to-br from-[#1a1f36] via-[#3d2f6e] to-[#6b4ce6]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 border-2 border-white/30 rotate-45"></div>
          <div className="absolute top-40 left-40 w-24 h-24 border-2 border-white/20 rotate-12"></div>
          <div className="absolute top-60 left-32 w-20 h-20 border-2 border-white/25 rotate-45"></div>
        </div>
        
        <div className="relative z-10 max-w-3xl pt-32">
          <h1 className="text-8xl font-bold text-white leading-tight">
            Modern iOS<br />Applications
          </h1>
        </div>
        
        <div className="relative z-10 max-w-md text-right pb-32">
          <p className="text-xl text-white font-light leading-relaxed">
            Enhancing mobile experience /<br />through innovative solutions
          </p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-16 bg-[#faf8f5]">
        <div className="max-w-7xl mx-auto flex gap-16 items-start">
          <div className="w-2/5">
            <div className="bg-white rounded-3xl p-12 shadow-lg">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-smartphone-line text-4xl text-[#6b4ce6]"></i>
              </div>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">PAKO d.o.o.</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                A team of experts developing modern applications for the iOS platform.
              </p>
            </div>
          </div>
          
          <div className="w-3/5 pt-16">
            <h2 className="text-6xl font-extrabold text-gray-900 mb-8 leading-tight">
              Expert iOS<br />Development
            </h2>
            <p className="text-lg text-gray-600 leading-loose">
              Our dedicated team combines years of experience with cutting-edge technology to deliver exceptional iOS applications. We specialize in creating secure, high-performance solutions that enhance user experience and improve the security of mobile devices. Every application we develop is crafted with meticulous attention to detail, ensuring reliability and user satisfaction.
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 px-16 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-light text-gray-500 tracking-widest uppercase mb-6 text-center">
            OUR PRODUCTS
          </p>
          <h2 className="text-6xl font-bold text-gray-900 text-center mb-20 leading-tight">
            Applications That<br />Make a Difference
          </h2>
          
          <div className="grid grid-cols-2 gap-10">
            {/* VPN Card */}
            <div className="bg-white rounded-2xl p-12 shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-shield-line text-4xl text-[#6b4ce6]"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">VPN Application</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Secure connection and privacy protection for your mobile device. Advanced encryption technology ensures safe browsing and protects your personal data from unauthorized access.
              </p>
            </div>
            
            {/* Cleaner Card */}
            <div className="bg-white rounded-2xl p-12 shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-brush-line text-4xl text-[#6b4ce6]"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Cleaner Application</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Optimize device performance and free up storage space. Intelligent cleaning algorithms maintain your device running smoothly while removing unnecessary files and cache data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-16 bg-[#f9f7f4]">
        <div className="max-w-7xl mx-auto">
          <p className="text-sm font-light text-gray-500 mb-4">WHY CHOOSE US</p>
          <h2 className="text-7xl font-extrabold text-gray-900 mb-20 leading-tight">
            Premium Quality<br />iOS Solutions
          </h2>
          
          <div className="grid grid-cols-3 gap-8">
            {/* Security Feature */}
            <div className="bg-[#ebe9e6] rounded-2xl p-10">
              <div className="w-12 h-12 flex items-center justify-center mb-5">
                <i className="ri-lock-line text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">Advanced Security</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Military-grade encryption and privacy protocols protect user data across all applications, ensuring complete confidentiality and peace of mind.
              </p>
            </div>
            
            {/* Performance Feature */}
            <div className="bg-[#ebe9e6] rounded-2xl p-10">
              <div className="w-12 h-12 flex items-center justify-center mb-5">
                <i className="ri-flashlight-line text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">Optimized Performance</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Lightweight architecture ensures minimal battery drain while delivering maximum functionality and speed for seamless user experience.
              </p>
            </div>
            
            {/* Experience Feature */}
            <div className="bg-[#ebe9e6] rounded-2xl p-10">
              <div className="w-12 h-12 flex items-center justify-center mb-5">
                <i className="ri-star-line text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">User-Centric Design</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Intuitive interfaces crafted through extensive user research provide seamless interaction experiences that users love.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative min-h-[600px] flex items-center justify-between px-16 overflow-hidden bg-gradient-to-br from-[#0a4d4e] to-[#1a5f5a]">
        <div className="absolute inset-0 opacity-5">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            ></div>
          ))}
        </div>
        
        <div className="relative z-10 max-w-3xl">
          <h2 className="text-7xl font-bold text-white leading-tight">
            Ready to Transform<br />Your Mobile Experience
          </h2>
        </div>
        
        <div className="relative z-10 max-w-md text-right">
          <p className="text-2xl text-white font-light leading-relaxed">
            Join thousands of satisfied users<br />worldwide
          </p>
        </div>
      </section>

      {/* Contact Footer */}
      <footer id="contact" className="bg-[#2d2d2d] pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-16">
          <div className="grid grid-cols-4 gap-12 mb-16">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/d6f638fe-d95c-4ddc-bf03-ccf15d6b67af.png" 
                  alt="PAKO d.o.o. Logo" 
                  className="h-8 w-8 object-contain"
                />
                <h3 className="text-xl font-bold text-white">PAKO d.o.o.</h3>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                Expert team developing<br />innovative iOS applications<br />for modern users
              </p>
            </div>
            
            {/* Location */}
            <div>
              <h4 className="text-xs font-light text-gray-500 tracking-widest uppercase mb-4">LOCATION</h4>
              <div className="space-y-3">
                <p className="text-sm text-white flex items-start gap-2">
                  <i className="ri-map-pin-line text-base mt-0.5"></i>
                  <span>Frana Galovica 4b</span>
                </p>
                <p className="text-sm text-white flex items-start gap-2">
                  <i className="ri-building-line text-base mt-0.5"></i>
                  <span>Koprivnica, 48000</span>
                </p>
                <p className="text-sm text-white flex items-start gap-2">
                  <i className="ri-earth-line text-base mt-0.5"></i>
                  <span>Croatia</span>
                </p>
              </div>
            </div>
            
            {/* Email */}
            <div>
              <h4 className="text-xs font-light text-gray-500 tracking-widest uppercase mb-4">EMAIL</h4>
              <a 
                href="mailto:information@pakodoo.com" 
                className="text-sm text-white hover:text-gray-300 transition-colors flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-mail-line text-base"></i>
                <span>information@pakodoo.com</span>
              </a>
            </div>
            
            {/* Phone */}
            <div>
              <h4 className="text-xs font-light text-gray-500 tracking-widest uppercase mb-4">PHONE</h4>
              <a 
                href="tel:+17542039885" 
                className="text-sm text-white hover:text-gray-300 transition-colors flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-phone-line text-base"></i>
                <span>+1 7542039885</span>
              </a>
            </div>
          </div>
          
          {/* Large Brand Text */}
          <div className="border-t border-gray-700 pt-12">
            <div className="text-center">
              <p className="text-9xl font-bold text-[#d4cfc4] tracking-wider">PAKO</p>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="mt-12 pt-8 border-t border-gray-700 text-center">
            <p className="text-sm text-gray-500">
              © 2025 PAKO d.o.o. All rights reserved. | 
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="ml-2 hover:text-gray-400 transition-colors cursor-pointer">
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
